import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { LogicError } from '../../../../shared/error';
import { OptionDataPayload } from '../../../../shared/options';
import { GenericOptionInput, ProductModel } from '../../../../shared/orders';
import { RaffleTicketStatus, raffleTicketStatus } from '../../../../shared/orders/raffle';
import {
  RegistrationFlagModel,
  RegistrationOptionInput,
  RegistrationUpsert,
} from '../../../../shared/registration/model';
import { RegistrationTicket, UserRegistration } from '../../../../shared/user/base';
import { ActionButtonModal, AttendanceFlags } from '../../../components';
import { ActionModal } from '../../../components/ActionModal';
import { UserSelector } from '../../../components/UserSelector';
import { RegistrationOptionRenderer } from '../../../modules/registration/ProductOptionViewerList';
import { useTranslation } from '../../../translations';
import {
  Fetcher,
  fthrow,
  isAccessError,
  isLogicError,
  isNonEmpty,
  NonEmptyList,
  useFetcher,
  useForm,
  useInputModel,
  useObject,
} from '../../../utils';
import { captureError } from '../../../utils/errorHandling';
import { cleanupRegOptions } from '../../../utils/options';
import { mapProductAddons } from '../../store/ProductConfigurator';

interface RegistrationCardProps {
  readonly userId: number;
  readonly registration?: UserRegistration;
  readonly bannedWords: string[];
  readonly complete: () => void;
  readonly close: () => void;
}

export const RegistrationEditModal: FC<RegistrationCardProps> = ({
  userId,
  registration,
  bannedWords,
  complete,
  close,
}) => {
  const { ts } = useTranslation();
  const productFetch = useFetcher(async (): Promise<{
    products: ProductModel[];
    flags: RegistrationFlagModel[];
    options: RegistrationOptionInput[];
  }> => {
    const category = (await api.getProductCategories()).find(
      ({ isRegistration }) => isRegistration,
    );

    const [products, form] = await Promise.all([
      category
        ? api
            .getFullProductsByCategory(category.id)
            .catch((error: Error) => (isAccessError(error) ? [] : fthrow(error)))
        : [],
      api.getRegistrationForm(),
    ]);

    return { products, flags: form.flags, options: form.options };
  }, []);

  const { data } = productFetch;

  return (
    <Modal className="modal-large" id="registrationCard" isOpen toggle={close}>
      <ModalHeader>{ts('edit_registration')}</ModalHeader>
      {data ? (
        isNonEmpty(data.products) ? (
          <RegistrationCardInner
            attendanceTypes={data.products}
            bannedWords={bannedWords}
            close={close}
            complete={complete}
            flags={data.flags}
            options={data.options}
            registration={registration}
            userId={userId}
          />
        ) : (
          <RegistrationNoProducts close={close} />
        )
      ) : (
        <Fetcher inline result={productFetch} />
      )}
    </Modal>
  );
};

const RegistrationNoProducts: FC<{ readonly close: () => void }> = ({ close }) => {
  const { ts } = useTranslation();
  return (
    <>
      <ModalBody>{ts('no_registration_products_available')}</ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={close}>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </>
  );
};

interface RegistrationCardInnerProps extends RegistrationCardProps {
  readonly attendanceTypes: NonEmptyList<ProductModel>;
  readonly flags: RegistrationFlagModel[];
  readonly options: RegistrationOptionInput[];
}

const RegistrationCardInner: FC<RegistrationCardInnerProps> = ({
  userId,
  registration,
  attendanceTypes,
  bannedWords,
  flags,
  options,
  complete,
  close,
}) => {
  const { ts } = useTranslation();
  const [reg, setReg] = useState<RegistrationUpsert>(() => ({
    attendanceTypeId:
      attendanceTypes.find(({ id }) => id === registration?.attendanceType.id)?.id ??
      attendanceTypes[0].id,
    badgeName: registration?.badgeName ?? '',
    options: registration?.options ?? {},
    flags: registration?.flags ?? [],
    raffle: registration?.raffleTicket,
    badgeArtId: registration?.badgeArtId ?? null,
  }));

  const [enabledFlags, setEnabledFlags] = useObject(reg, setReg, 'flags');
  const [raffle, setRaffle] = useObject(reg, setReg, 'raffle');
  const [showForceModal, setShowForceModal] = useState(false);
  const attendanceType = attendanceTypes.find(({ id }) => id === reg.attendanceTypeId)!;
  const setBadgeName = useInputModel(setReg, 'badgeName');

  useEffect(() => {
    if (attendanceType.raffle && !reg.raffle) {
      setRaffle({ status: 'waiting', points: 0 });
    } else if (!attendanceType.raffle && reg.raffle) {
      setRaffle(undefined);
    }
  }, [attendanceType, reg]);

  const form = useForm(async () => {
    const attendanceAndAddon = mapProductAddons(attendanceType.options, attendanceType.addons);
    const payload = cleanupRegOptions([...attendanceAndAddon, ...options], reg);
    try {
      await api.upsertRegistration(userId, payload);
      toast.success(ts('saved_successfully'));
    } catch (error) {
      if (
        isLogicError(error, LogicError.ProductNotAvailable) ||
        isLogicError(error, LogicError.InsufficientStock)
      ) {
        setShowForceModal(true);
        return;
      }

      throw error;
    }

    complete();
  }, [attendanceType.options, attendanceType.addons, options, reg, complete, userId, ts]);

  const submitForm = useCallback(() => {
    form.onSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.onSubmit]);

  return (
    <>
      {showForceModal && (
        <ForceModal
          close={() => {
            setShowForceModal(false);
          }}
          complete={complete}
          reg={cleanupRegOptions([...attendanceType.options, ...options], reg)}
          userId={userId}
        />
      )}
      <ModalBody>
        <form onSubmit={form.onSubmit}>
          <Row>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label for="badgeName">{ts('common.badge_name')}</Label>
                <Input
                  invalid={bannedWords.includes('badgeName')}
                  maxLength={32}
                  name="badgeName"
                  onChange={setBadgeName}
                  value={reg.badgeName}
                />
                {bannedWords.includes('badgeName') && (
                  <FormFeedback>{ts('badge_name_contains_a_banned')}</FormFeedback>
                )}
              </FormGroup>
            </Col>
            <Col lg={6} md={12}>
              <FormGroup>
                <Label for="attendanceType">{ts('attendance_type')}</Label>
                <Input
                  name="attendanceTypeId"
                  onChange={(e) => {
                    setReg({ ...reg, attendanceTypeId: Number.parseInt(e.target.value, 10) });
                  }}
                  type="select"
                  value={attendanceType.id}
                >
                  {attendanceTypes.map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            {flags.length > 0 && (
              <Col lg={12}>
                <FormGroup>
                  <AttendanceFlags
                    enabledFlags={enabledFlags}
                    flags={flags}
                    onUpdate={setEnabledFlags}
                  />
                </FormGroup>
              </Col>
            )}
            {options.length > 0 && (
              <Col lg={12}>
                <FormProductOptions
                  options={options.filter((t) => t.fromRegistration)}
                  payload={reg.options}
                  setRegistration={setReg}
                  title={ts('registration_options')}
                />
              </Col>
            )}
            {attendanceType.options.length > 0 && (
              <Col lg={12}>
                <FormProductOptions
                  options={attendanceType.options}
                  payload={reg.options}
                  setRegistration={setReg}
                  title={ts('product_options')}
                />
              </Col>
            )}
            {raffle && (
              <Col lg={12}>
                <UpdateRaffleComponent setTicket={setRaffle} ticket={raffle} />
              </Col>
            )}
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        {registration?.paidOrderItem && (
          <div style={{ flexGrow: 1 }}>
            <ActionButtonModal
              actionContent="common.delete"
              buttonContent="remove_payment"
              color="danger"
              id="deleteRegPayment"
              onComplete={async () => {
                await api.deleteRegistrationPayment(userId);
                toast.success(ts('payment_deleted'));
                complete();
              }}
              title="delete_registration_payment"
            >
              {ts('are_you_sure_you_want_to_delete_the_payment')}
            </ActionButtonModal>{' '}
            <TransferButton complete={complete} userId={userId} />
          </div>
        )}

        {registration && !registration.paidOrderItem && (
          <div style={{ flexGrow: 1 }}>
            <ActionButtonModal
              actionContent="common.delete"
              buttonContent="common.delete"
              color="danger"
              id="deleteReg"
              onComplete={async () => {
                await api.deleteRegistration(registration.id);
                toast.success(ts('registration_deleted'));
                complete();
              }}
              title="delete_registration"
            >
              {ts('are_you_sure_you_want_to_delete_the_registration')}
            </ActionButtonModal>
          </div>
        )}

        <Button color="primary" disabled={form.saving} id="submitUpdateReg" onClick={submitForm}>
          {ts(registration ? 'common.update' : 'common.create')} {ts('registration')}
        </Button>
        <Button color="secondary" onClick={close}>
          {ts('common.cancel')}
        </Button>
      </ModalFooter>
    </>
  );
};

interface TransferButtonProps {
  readonly userId: number;
  readonly complete: () => void;
}

const TransferButton: FC<TransferButtonProps> = ({ userId, complete }) => {
  const [ids, setIds] = useState<number[]>([]);
  const { ts } = useTranslation();

  return (
    <ActionButtonModal
      actionContent="transfer"
      buttonContent="transfer"
      color="secondary"
      id="transferPayment"
      onComplete={async () => {
        if (!ids[0]) {
          return;
        }

        await api.transferRegistration(userId, ids[0]);
        toast.success(ts('transfer_completed'));
        complete();
      }}
      title="transfer_registration_payment"
    >
      {ts('select_user_to_transfer_this')}
      <UserSelector id="transferSelect" maxItems={1} selectionIdsChanged={setIds} />
    </ActionButtonModal>
  );
};

interface FormProductOptionProps {
  readonly title: string;
  readonly options: GenericOptionInput[];
  readonly payload: OptionDataPayload;
  readonly setRegistration: Dispatch<SetStateAction<RegistrationUpsert>>;
}

const FormProductOptions: FC<FormProductOptionProps> = ({
  title,
  options,
  payload,
  setRegistration,
}) => {
  return (
    <>
      <hr />
      <FormGroup row>
        <Label md={4} sm={12}>
          {title}
        </Label>
        <Col md={8} sm={12}>
          {options.map((o) => (
            <Col key={o.id} xs={12}>
              <RegistrationOptionRenderer
                option={o}
                setRegistration={setRegistration}
                values={payload}
              />
            </Col>
          ))}
        </Col>
      </FormGroup>
    </>
  );
};

interface UpdateRaffleComponentProps {
  readonly ticket: RegistrationTicket;
  readonly setTicket: Dispatch<SetStateAction<RegistrationTicket | undefined>>;
}

const UpdateRaffleComponent: FC<UpdateRaffleComponentProps> = ({ ticket, setTicket }) => {
  const { ts } = useTranslation();
  return (
    <>
      <hr />
      <FormGroup row>
        <Label md={4} sm={12}>
          {ts('silo.options')}
        </Label>
        <Col lg={8} md={12}>
          <FormGroup>
            <Label for="raffle[status]">{ts('common.status')}</Label>
            <Input
              id="raffleStatus"
              name="raffle[status]"
              onChange={(e) => {
                setTicket({ ...ticket, status: e.target.value as RaffleTicketStatus });
              }}
              type="select"
              value={ticket.status}
            >
              {raffleTicketStatus.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="raffle[points]">{ts('points')}</Label>
            <Input
              maxLength={32}
              name="raffle[points]"
              onChange={(e) => {
                setTicket({ ...ticket, points: e.target.valueAsNumber });
              }}
              type="number"
              value={ticket.points}
            />
          </FormGroup>
        </Col>
      </FormGroup>
    </>
  );
};

interface ForceModalProps {
  readonly userId: number;
  readonly reg: RegistrationUpsert;
  readonly close: () => void;
  readonly complete: () => void;
}

const ForceModal: FC<ForceModalProps> = ({ userId, reg, close, complete }) => {
  const onComplete = useCallback(async () => {
    try {
      await api.upsertRegistration(userId, { ...reg, force: true });
      complete();
    } catch (error) {
      captureError(error as Error);
    }
  }, [complete, reg, userId]);

  const { ts } = useTranslation();
  return (
    <ActionModal
      actionContent="force_registration"
      close={close}
      color="danger"
      id="forceModal"
      isOpen
      onComplete={onComplete}
      title="force_registration"
    >
      <p>{ts('the_product_you_are_trying')}</p>
      <p>{ts('this_will_override_any_product')}</p>
    </ActionModal>
  );
};
